import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Alert,
  Snackbar,
  Stack,
  Card,
  CardContent,
  Tabs,
  Tab,
  CardActions,
  CircularProgress,
  Pagination,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  FileUpload as FileUploadIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  AdminPanelSettings as AdminIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Email as EmailIcon,
  MarkEmailRead as MarkEmailReadIcon,
  ErrorOutline as ErrorOutlineIcon,
  Schedule as ScheduleIcon,
  Login as LoginIcon,
} from '@mui/icons-material'
import { UserService } from '../services/userService'
import {
  User,
  UserFilters,
  UserStats,
  USER_ROLE_LABELS,
  CSVImportResponse,
  UserEmailDelivery,
} from '../types/user'
import { CSVImportModal } from '../components/user/CSVImportModal'
import { UserEditModal } from '../components/user/UserEditModal'
import { UserCreateModal } from '../components/user/UserCreateModal'
import { userGroupService, UserGroup } from '../services/userGroupService'
import UserGroupCreateModal from '../components/userGroup/UserGroupCreateModal'
import UserGroupEditModal from '../components/userGroup/UserGroupEditModal'
import AddMemberModal from '../components/userGroup/AddMemberModal'
import { SearchField } from '../components/common/SearchField'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

export const UserManagePage: React.FC = () => {
  const { t, i18n } = useTranslation('user')

  // タブ状態
  const [tabValue, setTabValue] = useState(0)

  // ====== ユーザー管理の状態 ======
  const [users, setUsers] = useState<User[]>([])
  const [stats, setStats] = useState<UserStats | null>(null)
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    limit: 20,
    // デフォルトはすべて表示（配信待ちの無効ユーザーも含む）
    isActive: undefined,
  })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  })
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    title: string
    message: string
    onConfirm: () => void
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  })
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'info',
  })
  const [csvImportModal, setCsvImportModal] = useState(false)
  const [editModal, setEditModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [createModal, setCreateModal] = useState(false)

  // ====== グループ管理の状態 ======
  const [userGroups, setUserGroups] = useState<UserGroup[]>([])
  const [groupLoading, setGroupLoading] = useState(true)
  const [groupError, setGroupError] = useState<string | null>(null)
  const [groupSearchTerm, setGroupSearchTerm] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('')
  const [groupPage, setGroupPage] = useState(1)
  const [groupTotalPages, setGroupTotalPages] = useState(1)
  const [departments, setDepartments] = useState<string[]>([])
  const [groupCreateModalOpen, setGroupCreateModalOpen] = useState(false)
  const [groupEditModalOpen, setGroupEditModalOpen] = useState(false)
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null)
  const [groupAnchorEl, setGroupAnchorEl] = useState<null | HTMLElement>(null)
  const [menuGroupId, setMenuGroupId] = useState<string | null>(null)

  // ====== 組織（部門）管理の状態 ======
  const [departmentList, setDepartmentList] = useState<{ name: string; count: number }[]>([])
  const [departmentLoading, setDepartmentLoading] = useState(true)
  const [editingDepartment, setEditingDepartment] = useState<string | null>(null)
  const [editDepartmentName, setEditDepartmentName] = useState('')

  // ====== 役職管理の状態 ======
  const [positionList, setPositionList] = useState<{ name: string; count: number }[]>([])
  const [positionLoading, setPositionLoading] = useState(true)
  const [editingPosition, setEditingPosition] = useState<string | null>(null)
  const [editPositionName, setEditPositionName] = useState('')

  // スナックバー表示
  const showSnackbar = useCallback(
    (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
      setSnackbar({ open: true, message, severity })
    },
    []
  )

  // ====== ユーザー管理の関数 ======
  const loadUserData = useCallback(async () => {
    try {
      const [usersResponse, statsResponse] = await Promise.all([
        UserService.getUsers(filters),
        UserService.getUserStats(),
      ])

      setUsers(usersResponse.users)
      setPagination(usersResponse.pagination)
      setStats(statsResponse)
    } catch (error) {
      console.error('データ読み込みエラー:', error)
      showSnackbar(t('messages.loadError'), 'error')
    }
  }, [filters, showSnackbar, t])

  useEffect(() => {
    if (tabValue === 0) {
      loadUserData()
    }
  }, [loadUserData, tabValue])

  const handleSearch = () => {
    setFilters((prev) => ({
      ...prev,
      search: searchTerm || undefined,
      page: 1,
    }))
  }

  const handleFilterChange = (key: keyof UserFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }))
  }

  const handlePageChange = (event: unknown, newPage: number) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage + 1,
    }))
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 1,
    }))
  }

  const handleSelectUser = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    )
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedUsers(users.map((user) => user.id))
    } else {
      setSelectedUsers([])
    }
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget)
    setSelectedUser(user)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedUser(null)
  }

  const handleDeleteUser = (user: User) => {
    setConfirmDialog({
      open: true,
      title: t('dialogs.deleteTitle'),
      message: t('dialogs.deleteMessage', { name: user.name, email: user.email }),
      onConfirm: async () => {
        try {
          await UserService.deleteUser(user.id)
          showSnackbar(t('messages.deleteSuccess'), 'success')
          loadUserData()
        } catch (error) {
          console.error('ユーザー削除エラー:', error)
          showSnackbar(t('messages.deleteError'), 'error')
        }
        setConfirmDialog((prev) => ({ ...prev, open: false }))
      },
    })
    handleMenuClose()
  }

  const handleDownloadTemplate = async () => {
    try {
      await UserService.downloadCSVTemplateToBrowser()
      showSnackbar(t('import.templateDownloaded'), 'success')
    } catch (error) {
      console.error('テンプレートダウンロードエラー:', error)
      showSnackbar(t('import.errors.templateFailed'), 'error')
    }
  }

  const handleCSVImportSuccess = (result: CSVImportResponse) => {
    const successCount = result.summary?.successCount ?? result.result.successCount ?? 0
    const errorCount = result.summary?.errorCount ?? result.result.errorCount ?? 0

    showSnackbar(
      t('import.success', { success: successCount, error: errorCount }),
      errorCount > 0 ? 'warning' : 'success'
    )
    loadUserData()
  }

  const handleBulkStatusChange = (isActive: boolean) => {
    const statusText = isActive ? t('status.active') : t('status.inactive')
    setConfirmDialog({
      open: true,
      title: t('bulk.statusChangeTitle'),
      message: t('bulk.statusChangeMessage', { count: selectedUsers.length, status: statusText }),
      onConfirm: async () => {
        try {
          await UserService.bulkUpdateStatus(selectedUsers, isActive)
          showSnackbar(
            isActive ? t('messages.activateSuccess') : t('messages.deactivateSuccess'),
            'success'
          )
          setSelectedUsers([])
          loadUserData()
        } catch (error: any) {
          console.error('一括ステータス変更エラー:', error)
          showSnackbar(t('messages.bulkStatusError'), 'error')
        }
        setConfirmDialog((prev) => ({ ...prev, open: false }))
      },
    })
  }

  const handleBulkDelete = () => {
    setConfirmDialog({
      open: true,
      title: t('bulk.deleteTitle'),
      message: t('bulk.deleteMessage', { count: selectedUsers.length }),
      onConfirm: async () => {
        try {
          await UserService.bulkDeleteUsers(selectedUsers)
          showSnackbar(t('messages.deleteSuccess'), 'success')
          setSelectedUsers([])
          loadUserData()
        } catch (error: any) {
          console.error('一括削除エラー:', error)
          showSnackbar(t('messages.bulkDeleteError'), 'error')
        }
        setConfirmDialog((prev) => ({ ...prev, open: false }))
      },
    })
  }

  const handleClearSelection = () => {
    setSelectedUsers([])
  }

  const handleEditUser = (user: User) => {
    setEditingUser(user)
    setEditModal(true)
    handleMenuClose()
  }

  const handleEditSuccess = (message: string) => {
    showSnackbar(message, 'success')
    loadUserData()
    setEditModal(false)
    setEditingUser(null)
  }

  const handleCreateSuccess = (user: User, message: string) => {
    showSnackbar(message, 'success')
    loadUserData()
    setCreateModal(false)
  }

  const getRoleColor = (
    role: string
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'secondary'
      case 'CORPORATE_ADMIN':
        return 'secondary'
      case 'USER':
        return 'primary'
      default:
        return 'default'
    }
  }

  // 配信状況のラベルと色を取得
  const getDeliveryStatusInfo = (
    delivery: UserEmailDelivery | null | undefined
  ): {
    label: string
    color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
    icon: React.ReactNode
    tooltip: string
  } => {
    const locale = i18n.language === 'ja' ? 'ja-JP' : 'en-US'

    if (!delivery) {
      return {
        label: '-',
        color: 'default',
        icon: null,
        tooltip: t('delivery.noInfo'),
      }
    }

    if (delivery.userLoggedIn) {
      return {
        label: t('delivery.loggedIn'),
        color: 'success',
        icon: <LoginIcon fontSize="small" />,
        tooltip: t('delivery.loggedInTooltip'),
      }
    }

    if (delivery.hasDeliveryIssue) {
      return {
        label: t('delivery.failed'),
        color: 'error',
        icon: <ErrorOutlineIcon fontSize="small" />,
        tooltip: t('delivery.failedTooltip'),
      }
    }

    if (delivery.isTimedOut) {
      return {
        label: t('delivery.timedOut'),
        color: 'warning',
        icon: <ScheduleIcon fontSize="small" />,
        tooltip: t('delivery.timedOutTooltip'),
      }
    }

    if (delivery.isTokenExpired) {
      return {
        label: t('delivery.tokenExpired'),
        color: 'warning',
        icon: <ScheduleIcon fontSize="small" />,
        tooltip: t('delivery.tokenExpiredTooltip'),
      }
    }

    switch (delivery.status) {
      case 'DELIVERED':
      case 'OPENED':
      case 'CLICKED':
        return {
          label: t('delivery.delivered'),
          color: 'success',
          icon: <MarkEmailReadIcon fontSize="small" />,
          tooltip: delivery.deliveredAt
            ? t('delivery.deliveredAt', {
                date: new Date(delivery.deliveredAt).toLocaleString(locale),
              })
            : t('delivery.deliveredTooltip'),
        }
      case 'SENT':
        return {
          label: t('delivery.sent'),
          color: 'info',
          icon: <EmailIcon fontSize="small" />,
          tooltip: t('delivery.sentTooltip'),
        }
      case 'PENDING':
        return {
          label: t('delivery.pending'),
          color: 'default',
          icon: <ScheduleIcon fontSize="small" />,
          tooltip: t('delivery.pendingTooltip'),
        }
      case 'BOUNCED':
        return {
          label: t('delivery.bounced'),
          color: 'error',
          icon: <ErrorOutlineIcon fontSize="small" />,
          tooltip: t('delivery.bouncedTooltip'),
        }
      case 'DROPPED':
        return {
          label: t('delivery.dropped'),
          color: 'error',
          icon: <ErrorOutlineIcon fontSize="small" />,
          tooltip: t('delivery.droppedTooltip'),
        }
      case 'DEFERRED':
        return {
          label: t('delivery.deferred'),
          color: 'warning',
          icon: <ScheduleIcon fontSize="small" />,
          tooltip: t('delivery.deferredTooltip'),
        }
      case 'SPAM_REPORT':
        return {
          label: t('delivery.spamReport'),
          color: 'error',
          icon: <ErrorOutlineIcon fontSize="small" />,
          tooltip: t('delivery.spamReportTooltip'),
        }
      case 'FAILED':
        return {
          label: t('delivery.failed'),
          color: 'error',
          icon: <ErrorOutlineIcon fontSize="small" />,
          tooltip: t('delivery.failedTooltip'),
        }
      default:
        return {
          label: t('delivery.unknown'),
          color: 'default',
          icon: null,
          tooltip: t('delivery.unknownTooltip'),
        }
    }
  }

  // ====== グループ管理の関数 ======
  const fetchUserGroups = useCallback(async () => {
    try {
      setGroupLoading(true)
      const response = await userGroupService.getUserGroups({
        page: groupPage,
        limit: 9,
        search: groupSearchTerm || undefined,
        department: departmentFilter || undefined,
      })

      setUserGroups(response.userGroups)
      setGroupTotalPages(response.pagination.totalPages)

      const deptSet = new Set(response.userGroups.map((g) => g.department).filter(Boolean))
      const depts = Array.from(deptSet) as string[]
      setDepartments(depts)

      setGroupError(null)
    } catch (err) {
      setGroupError(t('messages.groupLoadError'))
    } finally {
      setGroupLoading(false)
    }
  }, [groupPage, groupSearchTerm, departmentFilter, t])

  useEffect(() => {
    if (tabValue === 1) {
      fetchUserGroups()
    }
  }, [tabValue, fetchUserGroups])

  useEffect(() => {
    const timer = setTimeout(() => {
      setGroupPage(1)
    }, 500)
    return () => clearTimeout(timer)
  }, [groupSearchTerm, departmentFilter])

  const fetchGroupDetail = async (groupId: string) => {
    try {
      const response = await userGroupService.getUserGroupById(groupId)
      setSelectedGroup(response.userGroup)
      setDetailModalOpen(true)
    } catch (err) {
      setGroupError(t('messages.groupDetailError'))
    }
  }

  const handleDeleteGroup = async (groupId: string) => {
    if (!window.confirm(t('dialogs.deleteGroupConfirm'))) return

    try {
      await userGroupService.deleteUserGroup(groupId)
      await fetchUserGroups()
      setGroupAnchorEl(null)
      showSnackbar(t('messages.groupDeleteSuccess'), 'success')
    } catch (err) {
      setGroupError(t('messages.groupDeleteError'))
    }
  }

  const handleRemoveMember = async (groupId: string, memberId: string) => {
    if (!window.confirm(t('dialogs.removeMemberConfirm'))) return

    try {
      await userGroupService.removeMemberFromGroup(groupId, memberId)
      if (selectedGroup && selectedGroup.id === groupId) {
        await fetchGroupDetail(groupId)
      }
      await fetchUserGroups()
      showSnackbar(t('messages.memberRemoveSuccess'), 'success')
    } catch (err) {
      setGroupError(t('messages.memberRemoveError'))
    }
  }

  const handleGroupMenuOpen = (event: React.MouseEvent<HTMLElement>, groupId: string) => {
    setGroupAnchorEl(event.currentTarget)
    setMenuGroupId(groupId)
  }

  const handleGroupMenuClose = () => {
    setGroupAnchorEl(null)
    setMenuGroupId(null)
  }

  const handleGroupCreateSuccess = async () => {
    setGroupCreateModalOpen(false)
    await fetchUserGroups()
    showSnackbar(t('messages.groupCreateSuccess'), 'success')
  }

  const handleGroupEditSuccess = async () => {
    setGroupEditModalOpen(false)
    setSelectedGroup(null)
    await fetchUserGroups()
    showSnackbar(t('messages.groupUpdateSuccess'), 'success')
  }

  const handleAddMemberSuccess = async () => {
    setAddMemberModalOpen(false)
    if (selectedGroup) {
      await fetchGroupDetail(selectedGroup.id)
    }
    await fetchUserGroups()
    showSnackbar(t('messages.memberAddSuccess'), 'success')
  }

  // グループ統計情報
  const groupStats = {
    totalGroups: userGroups.length,
    totalMembers: userGroups.reduce((sum, g) => sum + (g._count?.members || 0), 0),
  }

  // ====== 組織（部門）管理の関数 ======
  const fetchDepartments = useCallback(async () => {
    try {
      setDepartmentLoading(true)
      const statsData = await UserService.getUserStats()
      const deptData = statsData.departmentDistribution.map((d) => ({
        name: d.department,
        count: d.count,
      }))
      setDepartmentList(deptData)
    } catch (err) {
      console.error('部門一覧取得エラー:', err)
      showSnackbar(t('messages.departmentLoadError'), 'error')
    } finally {
      setDepartmentLoading(false)
    }
  }, [showSnackbar, t])

  useEffect(() => {
    if (tabValue === 2) {
      fetchDepartments()
    }
  }, [tabValue, fetchDepartments])

  const handleRenameDepartment = async (oldName: string, newName: string) => {
    if (!newName.trim() || oldName === newName) {
      setEditingDepartment(null)
      return
    }
    try {
      // 該当部門の全ユーザーを取得して更新
      const response = await UserService.getUsers({ department: oldName, limit: 1000 })
      const userIds = response.users.map((u) => u.id)
      if (userIds.length > 0) {
        await UserService.bulkUpdateUsers({ userIds, updates: { department: newName.trim() } })
      }
      showSnackbar(t('messages.departmentRenameSuccess', { name: newName }), 'success')
      setEditingDepartment(null)
      fetchDepartments()
      loadUserData()
    } catch (err) {
      console.error('部門名変更エラー:', err)
      showSnackbar(t('messages.departmentRenameError'), 'error')
    }
  }

  const handleDeleteDepartment = async (deptName: string) => {
    if (!window.confirm(t('organizations.deleteConfirm', { name: deptName }))) return
    try {
      const response = await UserService.getUsers({ department: deptName, limit: 1000 })
      const userIds = response.users.map((u) => u.id)
      if (userIds.length > 0) {
        await UserService.bulkUpdateUsers({ userIds, updates: { department: '' } })
      }
      showSnackbar(t('messages.departmentDeleteSuccess', { name: deptName }), 'success')
      fetchDepartments()
      loadUserData()
    } catch (err) {
      console.error('部門削除エラー:', err)
      showSnackbar(t('messages.departmentDeleteError'), 'error')
    }
  }

  // ====== 役職管理の関数 ======
  const fetchPositions = useCallback(async () => {
    try {
      setPositionLoading(true)
      // ユーザー一覧から役職を集計
      const response = await UserService.getUsers({ limit: 1000 })
      const positionMap = new Map<string, number>()
      response.users.forEach((u) => {
        if (u.position) {
          positionMap.set(u.position, (positionMap.get(u.position) || 0) + 1)
        }
      })
      const posData = Array.from(positionMap.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => a.name.localeCompare(b.name, 'ja'))
      setPositionList(posData)
    } catch (err) {
      console.error('役職一覧取得エラー:', err)
      showSnackbar(t('messages.positionLoadError'), 'error')
    } finally {
      setPositionLoading(false)
    }
  }, [showSnackbar, t])

  useEffect(() => {
    if (tabValue === 3) {
      fetchPositions()
    }
  }, [tabValue, fetchPositions])

  const handleRenamePosition = async (oldName: string, newName: string) => {
    if (!newName.trim() || oldName === newName) {
      setEditingPosition(null)
      return
    }
    try {
      const response = await UserService.getUsers({ limit: 1000 })
      const userIds = response.users.filter((u) => u.position === oldName).map((u) => u.id)
      if (userIds.length > 0) {
        await UserService.bulkUpdateUsers({ userIds, updates: { position: newName.trim() } })
      }
      showSnackbar(t('messages.positionRenameSuccess', { name: newName }), 'success')
      setEditingPosition(null)
      fetchPositions()
      loadUserData()
    } catch (err) {
      console.error('役職名変更エラー:', err)
      showSnackbar(t('messages.positionRenameError'), 'error')
    }
  }

  const handleDeletePosition = async (posName: string) => {
    if (!window.confirm(t('positions.deleteConfirm', { name: posName }))) return
    try {
      const response = await UserService.getUsers({ limit: 1000 })
      const userIds = response.users.filter((u) => u.position === posName).map((u) => u.id)
      if (userIds.length > 0) {
        await UserService.bulkUpdateUsers({ userIds, updates: { position: '' } })
      }
      showSnackbar(t('messages.positionDeleteSuccess', { name: posName }), 'success')
      fetchPositions()
      loadUserData()
    } catch (err) {
      console.error('役職削除エラー:', err)
      showSnackbar(t('messages.positionDeleteError'), 'error')
    }
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('title')}
      </Typography>

      {/* 統計情報 - タブ共通 */}
      {stats && (
        <Stack direction="row" spacing={3} sx={{ mb: 3 }} flexWrap="wrap">
          <Card sx={{ flex: 1, minWidth: 150 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                {t('stats.totalUsers')}
              </Typography>
              <Typography variant="h4">{stats.totalUsers}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 150 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                {t('stats.activeUsers')}
              </Typography>
              <Typography variant="h4">{stats.activeUsers}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 150 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                {t('stats.departments')}
              </Typography>
              <Typography variant="h4">{stats.departmentDistribution.length}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 150 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                {t('stats.groups')}
              </Typography>
              <Typography variant="h4">{groupStats.totalGroups}</Typography>
            </CardContent>
          </Card>
        </Stack>
      )}

      {/* タブナビゲーション */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label={t('title')}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label={t('tabs.users')}
            id="user-tab-0"
            aria-controls="user-tabpanel-0"
          />
          <Tab
            icon={<GroupIcon />}
            iconPosition="start"
            label={t('tabs.groups')}
            id="user-tab-1"
            aria-controls="user-tabpanel-1"
          />
          <Tab
            icon={<BusinessIcon />}
            iconPosition="start"
            label={t('tabs.organizations')}
            id="user-tab-2"
            aria-controls="user-tabpanel-2"
          />
          <Tab
            icon={<WorkIcon />}
            iconPosition="start"
            label={t('tabs.positions')}
            id="user-tab-3"
            aria-controls="user-tabpanel-3"
          />
        </Tabs>
      </Paper>

      {/* ユーザー一覧タブ */}
      <TabPanel value={tabValue} index={0}>
        {/* 操作パネル */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <SearchField
              value={searchTerm}
              onChange={setSearchTerm}
              onSearch={handleSearch}
              placeholder={t('list.searchPlaceholder')}
              sx={{ minWidth: 300 }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('list.columns.status')}</InputLabel>
              <Select
                value={filters.isActive === undefined ? '' : filters.isActive.toString()}
                label={t('list.columns.status')}
                onChange={(e) =>
                  handleFilterChange(
                    'isActive',
                    e.target.value === '' ? undefined : e.target.value === 'true'
                  )
                }
              >
                <MenuItem value="">{t('list.filter.all')}</MenuItem>
                <MenuItem value="true">{t('list.filter.active')}</MenuItem>
                <MenuItem value="false">{t('list.filter.inactive')}</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateModal(true)}
            >
              {t('create.newUser')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={() => setCsvImportModal(true)}
            >
              {t('import.csvImport')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
            >
              {t('import.downloadTemplate')}
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadUserData}>
              {t('actions.refresh')}
            </Button>
          </Stack>
        </Paper>

        {/* 一括操作ツールバー */}
        {selectedUsers.length > 0 && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                {t('list.selectedCount', { count: selectedUsers.length })}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleBulkStatusChange(true)}
              >
                {t('actions.makeActive')}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<CancelIcon />}
                onClick={() => handleBulkStatusChange(false)}
              >
                {t('actions.makeInactive')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                sx={{
                  color: 'grey.300',
                  borderColor: 'grey.500',
                  '&:hover': { borderColor: '#EF4444', color: '#EF4444' },
                }}
              >
                {t('actions.delete')}
              </Button>
              <IconButton
                size="small"
                onClick={handleClearSelection}
                sx={{ color: 'primary.contrastText' }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </Paper>
        )}

        {/* ユーザーテーブル */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedUsers.length > 0 && selectedUsers.length < users.length
                      }
                      checked={users.length > 0 && selectedUsers.length === users.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>{t('list.columns.name')}</TableCell>
                  <TableCell>{t('list.columns.email')}</TableCell>
                  <TableCell>{t('list.columns.department')}</TableCell>
                  <TableCell>{t('list.columns.position')}</TableCell>
                  <TableCell>{t('list.columns.role')}</TableCell>
                  <TableCell>{t('list.columns.status')}</TableCell>
                  <TableCell>{t('list.columns.initialEmail')}</TableCell>
                  <TableCell>{t('list.columns.enrollments')}</TableCell>
                  <TableCell>{t('list.columns.createdAt')}</TableCell>
                  <TableCell align="right">{t('common:buttons.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                      />
                    </TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.department || '-'}</TableCell>
                    <TableCell>{user.position || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={USER_ROLE_LABELS[user.role as keyof typeof USER_ROLE_LABELS]}
                        color={getRoleColor(user.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? t('status.active') : t('status.inactive')}
                        color={user.isActive ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const deliveryInfo = getDeliveryStatusInfo(user.emailDelivery)
                        return (
                          <Tooltip title={deliveryInfo.tooltip} arrow>
                            <Chip
                              icon={deliveryInfo.icon as React.ReactElement | undefined}
                              label={deliveryInfo.label}
                              color={deliveryInfo.color}
                              size="small"
                              variant={deliveryInfo.label === '-' ? 'outlined' : 'filled'}
                            />
                          </Tooltip>
                        )
                      })()}
                    </TableCell>
                    <TableCell>
                      {user._count
                        ? t('groups.members.enrollments', { count: user._count.enrollments })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {new Date(user.createdAt).toLocaleDateString(
                        i18n.language === 'ja' ? 'ja-JP' : 'en-US'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, user)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page - 1}
            onPageChange={handlePageChange}
            rowsPerPage={pagination.limit}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage={t('pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) =>
              t('pagination.displayedRows', { from, to, count })
            }
          />
        </Paper>
      </TabPanel>

      {/* グループタブ */}
      <TabPanel value={tabValue} index={1}>
        {/* 操作パネル */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <SearchField
              value={groupSearchTerm}
              onChange={setGroupSearchTerm}
              placeholder={t('groups.searchPlaceholder')}
              sx={{ minWidth: 300 }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('groups.departmentFilter')}</InputLabel>
              <Select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                label={t('groups.departmentFilter')}
              >
                <MenuItem value="">{t('list.filter.all')}</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setGroupCreateModalOpen(true)}
            >
              {t('groups.newGroup')}
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchUserGroups}>
              {t('actions.refresh')}
            </Button>
          </Stack>
        </Paper>

        {/* エラー表示 */}
        {groupError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setGroupError(null)}>
            {groupError}
          </Alert>
        )}

        {/* グループカード一覧 */}
        {groupLoading && userGroups.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : userGroups.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
            }}
          >
            <GroupIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('groups.list.noGroups')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {groupSearchTerm || departmentFilter
                ? t('groups.list.noGroupsSearch')
                : t('groups.list.noGroupsCreate')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setGroupCreateModalOpen(true)}
            >
              {t('groups.create.button')}
            </Button>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 3,
              }}
            >
              {userGroups.map((group) => (
                <Box key={group.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          component="h2"
                          sx={{ wordBreak: 'break-word', flexGrow: 1 }}
                        >
                          {group.name}
                        </Typography>
                        <IconButton size="small" onClick={(e) => handleGroupMenuOpen(e, group.id)}>
                          <MoreVertIcon />
                        </IconButton>
                      </Box>

                      {group.department && (
                        <Chip
                          label={group.department}
                          size="small"
                          variant="outlined"
                          sx={{ mb: 1 }}
                        />
                      )}

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {group.description || t('groups.detail.noDescription')}
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {t('groups.members.count', { count: group._count?.members || 0 })}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AssignmentIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {t('groups.members.enrollments', {
                              count: group._count?.enrollments || 0,
                            })}
                          </Typography>
                        </Box>
                      </Box>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        {t('groups.detail.creator', { name: group.creator.name })}
                      </Typography>
                    </CardContent>

                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => fetchGroupDetail(group.id)}
                      >
                        {t('groups.actions.viewDetails')}
                      </Button>
                    </CardActions>
                  </Card>
                </Box>
              ))}
            </Box>

            {/* ページネーション */}
            {groupTotalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={groupTotalPages}
                  page={groupPage}
                  onChange={(_, value) => setGroupPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </TabPanel>

      {/* 組織（部門）タブ */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
          >
            <Typography variant="h6">
              <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('organizations.title')}
            </Typography>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchDepartments}>
              {t('actions.refresh')}
            </Button>
          </Box>

          {departmentLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : departmentList.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 2,
              }}
            >
              <BusinessIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('organizations.noOrganizations')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('organizations.noOrganizationsHint')}
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('organizations.name')}</TableCell>
                  <TableCell align="center">{t('organizations.memberCount')}</TableCell>
                  <TableCell align="right">{t('common:buttons.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {departmentList.map((dept) => (
                  <TableRow key={dept.name} hover>
                    <TableCell>
                      {editingDepartment === dept.name ? (
                        <TextField
                          size="small"
                          value={editDepartmentName}
                          onChange={(e) => setEditDepartmentName(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleRenameDepartment(dept.name, editDepartmentName)
                            }
                          }}
                          onBlur={() => handleRenameDepartment(dept.name, editDepartmentName)}
                          autoFocus
                          sx={{ minWidth: 200 }}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography>{dept.name}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={`${dept.count}名`} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('organizations.edit')}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingDepartment(dept.name)
                            setEditDepartmentName(dept.name)
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('organizations.delete')}>
                        <IconButton
                          size="small"
                          sx={{ color: 'text.secondary', '&:hover': { color: '#EF4444' } }}
                          onClick={() => handleDeleteDepartment(dept.name)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Alert severity="info" sx={{ mt: 3 }}>
            {t('organizations.info')}
          </Alert>
        </Paper>
      </TabPanel>

      {/* 役職タブ */}
      <TabPanel value={tabValue} index={3}>
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
          >
            <Typography variant="h6">
              <WorkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('positions.title')}
            </Typography>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchPositions}>
              {t('actions.refresh')}
            </Button>
          </Box>

          {positionLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : positionList.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 2,
              }}
            >
              <WorkIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('positions.noPositions')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('positions.noPositionsHint')}
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('positions.name')}</TableCell>
                  <TableCell align="center">{t('positions.memberCount')}</TableCell>
                  <TableCell align="right">{t('common:buttons.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {positionList.map((pos) => (
                  <TableRow key={pos.name} hover>
                    <TableCell>
                      {editingPosition === pos.name ? (
                        <TextField
                          size="small"
                          value={editPositionName}
                          onChange={(e) => setEditPositionName(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleRenamePosition(pos.name, editPositionName)
                            }
                          }}
                          onBlur={() => handleRenamePosition(pos.name, editPositionName)}
                          autoFocus
                          sx={{ minWidth: 200 }}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WorkIcon fontSize="small" color="action" />
                          <Typography>{pos.name}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={`${pos.count}名`} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('positions.edit')}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingPosition(pos.name)
                            setEditPositionName(pos.name)
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('positions.delete')}>
                        <IconButton
                          size="small"
                          sx={{ color: 'text.secondary', '&:hover': { color: '#EF4444' } }}
                          onClick={() => handleDeletePosition(pos.name)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Alert severity="info" sx={{ mt: 3 }}>
            {t('positions.info')}
          </Alert>
        </Paper>
      </TabPanel>

      {/* ====== ユーザー管理のモーダル・メニュー ====== */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => selectedUser && handleEditUser(selectedUser)}>
          <EditIcon sx={{ mr: 1 }} />
          {t('actions.edit')}
        </MenuItem>
        <MenuItem
          onClick={() => selectedUser && handleDeleteUser(selectedUser)}
          disabled={selectedUser?.role === 'SUPER_ADMIN'}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          {t('actions.delete')}
        </MenuItem>
      </Menu>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}>
            {t('dialogs.cancel')}
          </Button>
          <Button onClick={confirmDialog.onConfirm} color="primary" autoFocus>
            {t('dialogs.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <CSVImportModal
        open={csvImportModal}
        onClose={() => setCsvImportModal(false)}
        onSuccess={handleCSVImportSuccess}
      />

      <UserEditModal
        open={editModal}
        user={editingUser}
        onClose={() => {
          setEditModal(false)
          setEditingUser(null)
        }}
        onSuccess={handleEditSuccess}
      />

      <UserCreateModal
        open={createModal}
        onClose={() => setCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* ====== グループ管理のモーダル・メニュー ====== */}
      <Menu anchorEl={groupAnchorEl} open={Boolean(groupAnchorEl)} onClose={handleGroupMenuClose}>
        <MenuItem
          onClick={() => {
            const group = userGroups.find((g) => g.id === menuGroupId)
            if (group) {
              setSelectedGroup(group)
              setGroupEditModalOpen(true)
            }
            handleGroupMenuClose()
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          {t('actions.edit')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            const group = userGroups.find((g) => g.id === menuGroupId)
            if (group) {
              setSelectedGroup(group)
              setAddMemberModalOpen(true)
            }
            handleGroupMenuClose()
          }}
        >
          <PersonIcon sx={{ mr: 1 }} />
          {t('groups.actions.addMember')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuGroupId) {
              handleDeleteGroup(menuGroupId)
            }
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          {t('actions.delete')}
        </MenuItem>
      </Menu>

      {/* グループ詳細モーダル */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon />
            {selectedGroup?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedGroup && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedGroup.description || t('groups.detail.noDescription')}
              </Typography>

              {selectedGroup.department && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('groups.detail.department', { name: selectedGroup.department })}
                </Typography>
              )}

              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('groups.detail.memberCount', { count: selectedGroup.members.length })}
              </Typography>

              <List>
                {selectedGroup.members.map((member) => (
                  <ListItem key={member.id}>
                    <Avatar sx={{ mr: 2 }}>{member.user.name.charAt(0)}</Avatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {member.user.name}
                          {member.role === 'ADMIN' && (
                            <Tooltip title={t('common:roles.admin')}>
                              <AdminIcon fontSize="small" color="primary" />
                            </Tooltip>
                          )}
                        </Box>
                      }
                      secondary={`${member.user.email} ${member.user.department ? `(${member.user.department})` : ''}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveMember(selectedGroup.id, member.id)}
                        sx={{ color: 'text.secondary', '&:hover': { color: '#EF4444' } }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>

              {selectedGroup.members.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 3 }}
                >
                  {t('groups.detail.noMembers')}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailModalOpen(false)}>{t('groups.actions.close')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              setDetailModalOpen(false)
              setAddMemberModalOpen(true)
            }}
          >
            {t('groups.actions.addMember')}
          </Button>
        </DialogActions>
      </Dialog>

      <UserGroupCreateModal
        open={groupCreateModalOpen}
        onClose={() => setGroupCreateModalOpen(false)}
        onSuccess={handleGroupCreateSuccess}
      />

      {selectedGroup && (
        <UserGroupEditModal
          open={groupEditModalOpen}
          onClose={() => {
            setGroupEditModalOpen(false)
            setSelectedGroup(null)
          }}
          onSuccess={handleGroupEditSuccess}
          userGroup={selectedGroup}
        />
      )}

      {selectedGroup && (
        <AddMemberModal
          open={addMemberModalOpen}
          onClose={() => setAddMemberModalOpen(false)}
          onSuccess={handleAddMemberSuccess}
          userGroup={selectedGroup}
        />
      )}

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
